(function () {
  'use strict';

  var drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  // Make sure the drawer is visible (Liquid renders with display:none initially)
  drawer.style.display = '';

  // --------------------------------------------------------------------------
  // Internal state
  // --------------------------------------------------------------------------
  var controller = new AbortController();
  var signal = controller.signal;
  var lastFocusedElement = null;
  var currentCart = null;
  var activeCurrency = null;

  var freeShippingThreshold = parseFloat(drawer.getAttribute('data-free-shipping-threshold')) || 0;

  // --------------------------------------------------------------------------
  // Currency / money helpers
  // --------------------------------------------------------------------------
  function getActiveCurrency() {
    if (activeCurrency) return activeCurrency;
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    return 'USD';
  }

  function formatMoney(cents) {
    var currency = getActiveCurrency();
    var amount = (Number(cents) || 0) / 100;
    var isWhole = amount % 1 === 0;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: isWhole ? 0 : 2
      }).format(amount);
    } catch (e) {
      return (isWhole ? amount.toFixed(0) : amount.toFixed(2)) + ' ' + currency;
    }
  }

  // --------------------------------------------------------------------------
  // Drawer open / close + focus trap + body scroll lock
  // --------------------------------------------------------------------------
  function getFocusableElements() {
    var panel = drawer.querySelector('.oddit-cart-drawer__panel');
    if (!panel) return [];
    return Array.prototype.slice.call(
      panel.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(function (el) {
      return el.offsetParent !== null;
    });
  }

  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    var focusable = getFocusableElements();
    if (focusable.length === 0) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function openDrawer() {
    lastFocusedElement = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Move focus into the drawer
    setTimeout(function () {
      var closeBtn = drawer.querySelector('.oddit-cart-drawer__close');
      var focusable = getFocusableElements();
      var target = closeBtn || focusable[0];
      if (target) target.focus();
    }, 50);

    // Refresh cart on open to ensure server state is reflected
    getCart()
      .then(function (cart) {
        renderCart(cart);
      })
      .catch(function () {
        /* fail silently — drawer still opens with last-known state */
      });
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
  }

  function toggleDrawer() {
    if (drawer.classList.contains('is-open')) {
      closeDrawer();
    } else {
      openDrawer();
    }
  }

  // --------------------------------------------------------------------------
  // State container management (empty vs filled)
  // --------------------------------------------------------------------------
  function setState(itemCount) {
    var emptyEl = drawer.querySelector('[data-cart-empty]');
    var filledFooter = drawer.querySelector('[data-cart-filled]');
    var itemsContainer = drawer.querySelector('[data-cart-items]');

    var isEmpty = !itemCount || itemCount <= 0;

    if (emptyEl) emptyEl.hidden = !isEmpty;

    if (filledFooter) {
      filledFooter.style.display = isEmpty ? 'none' : '';
    }

    if (itemsContainer) {
      itemsContainer.classList.toggle('is-empty', isEmpty);
      itemsContainer.hidden = isEmpty;
    }

    // Recommended add-ons are only relevant once the cart has items.
    var addonsEl = drawer.querySelector('[data-cross-sell-container]');
    if (addonsEl) addonsEl.hidden = isEmpty;

    drawer.classList.toggle('cart-is-empty', isEmpty);
    drawer.classList.toggle('cart-has-items', !isEmpty);

    // Generic state slots: [data-cart-state="empty|filled"]
    var stateNodes = drawer.querySelectorAll('[data-cart-state]');
    stateNodes.forEach(function (node) {
      var state = node.getAttribute('data-cart-state');
      var show = (isEmpty && state === 'empty') || (!isEmpty && state !== 'empty');
      node.hidden = !show;
    });
  }

  // --------------------------------------------------------------------------
  // Cart AJAX API
  // --------------------------------------------------------------------------
  function jsonHeaders() {
    return {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    };
  }

  function handleResponse(response) {
    if (!response.ok) {
      return response
        .json()
        .catch(function () {
          return { description: response.statusText };
        })
        .then(function (err) {
          var message =
            (err && (err.description || err.message)) ||
            'Cart request failed (' + response.status + ')';
          var error = new Error(message);
          error.response = err;
          error.status = response.status;
          throw error;
        });
    }
    return response.json();
  }

  function getCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: jsonHeaders(),
      credentials: 'same-origin'
    }).then(handleResponse);
  }

  function addToCart(variantId, quantity, properties) {
    quantity = quantity || 1;
    properties = properties || {};
    var body = {
      id: Number(variantId),
      quantity: Number(quantity),
      properties: properties
    };

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: jsonHeaders(),
      credentials: 'same-origin',
      body: JSON.stringify(body)
    })
      .then(handleResponse)
      .then(function () {
        return getCart();
      })
      .then(function (cart) {
        renderCart(cart);
        openDrawer();
        return cart;
      });
  }

  function addMultipleToCart(items) {
    var payload = {
      items: (items || []).map(function (item) {
        return {
          id: Number(item.id),
          quantity: Number(item.quantity || 1),
          properties: item.properties || {}
        };
      })
    };

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: jsonHeaders(),
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    })
      .then(handleResponse)
      .then(function () {
        return getCart();
      })
      .then(function (cart) {
        renderCart(cart);
        openDrawer();
        return cart;
      });
  }

  function updateLineItemQuantity(lineItemKey, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: jsonHeaders(),
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineItemKey, quantity: Number(quantity) })
    })
      .then(handleResponse)
      .then(function (cart) {
        renderCart(cart);
        return cart;
      });
  }

  function removeFromCart(lineItemKey) {
    return updateLineItemQuantity(lineItemKey, 0);
  }

  function updateLineItemToSubscription(lineItemKey, sellingPlanId) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: jsonHeaders(),
      credentials: 'same-origin',
      body: JSON.stringify({
        id: lineItemKey,
        selling_plan: sellingPlanId ? Number(sellingPlanId) : null
      })
    })
      .then(handleResponse)
      .then(function (cart) {
        renderCart(cart);
        return cart;
      });
  }

  function refreshCart() {
    return getCart().then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  function collectDiscountApplications(cart) {
    var applications = (cart.cart_level_discount_applications || []).slice();
    var seen = {};

    applications.forEach(function (application) {
      seen[String(application.title || '').toLowerCase()] = true;
    });

    (cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (allocation) {
        var application = allocation.discount_application;
        if (!application) return;
        var key = String(application.title || '').toLowerCase();
        if (seen[key]) return;
        seen[key] = true;
        applications.push(application);
      });
    });

    return applications;
  }

  function findDiscountByCode(cart, code) {
    var wanted = String(code || '').trim().toLowerCase();
    if (!wanted) return null;

    var applications = collectDiscountApplications(cart);
    for (var i = 0; i < applications.length; i++) {
      if (String(applications[i].title || '').trim().toLowerCase() === wanted) {
        return applications[i];
      }
    }
    return null;
  }

  // Codes the shopper entered themselves. Automatic and script discounts have no
  // code to re-send, so they are excluded from the set we write back.
  function collectDiscountCodes(cart) {
    return collectDiscountApplications(cart)
      .filter(function (application) {
        return application.type === 'discount_code';
      })
      .map(function (application) {
        return String(application.title || '').trim();
      })
      .filter(Boolean);
  }

  // /cart/update.js replaces the whole discount set, so every write sends the full
  // list of codes that should remain on the cart.
  function setDiscountCodes(codes) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: codes.join(',') })
    })
      .then(handleResponse)
      .then(function () {
        return getCart();
      });
  }

  function applyDiscountCode(code) {
    return getCart().then(function (cart) {
      var codes = collectDiscountCodes(cart);
      var wanted = String(code || '').trim().toLowerCase();
      var alreadyApplied = codes.some(function (existing) {
        return existing.toLowerCase() === wanted;
      });

      if (!alreadyApplied) codes.push(String(code).trim());
      return setDiscountCodes(codes);
    });
  }

  function removeDiscountCode(code) {
    return getCart().then(function (cart) {
      var unwanted = String(code || '').trim().toLowerCase();
      var codes = collectDiscountCodes(cart).filter(function (existing) {
        return existing.toLowerCase() !== unwanted;
      });

      return setDiscountCodes(codes);
    });
  }

  function setDiscountMessage(text, isError) {
    var messageEl = drawer.querySelector('[data-cart-discount-message]');
    if (!messageEl) return;

    if (!text) {
      messageEl.textContent = '';
      messageEl.hidden = true;
      messageEl.classList.remove('oddit-cart-drawer__discount-message--error');
      return;
    }

    messageEl.textContent = text;
    messageEl.hidden = false;
    messageEl.classList.toggle('oddit-cart-drawer__discount-message--error', !!isError);
  }

  function updateDiscountUI(cart) {
    var list = drawer.querySelector('[data-cart-discount-applied]');
    if (!list) return;

    var applications = collectDiscountApplications(cart);
    if (!applications.length) {
      list.innerHTML = '';
      list.hidden = true;
      return;
    }

    list.innerHTML = applications
      .map(function (application) {
        return (
          '<li class="oddit-cart-drawer__discount-chip">' +
            '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
              '<path d="M13.3 8.7 8.7 13.3a1 1 0 0 1-1.4 0L2 8V2h6l5.3 5.3a1 1 0 0 1 0 1.4Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
              '<circle cx="5.5" cy="5.5" r="1" fill="currentColor"/>' +
            '</svg>' +
            '<span class="oddit-cart-drawer__discount-chip-code">' +
              escapeHtml(application.title) +
            '</span>' +
            '<span class="oddit-cart-drawer__discount-chip-amount">&minus;' +
              formatMoney(application.total_allocated_amount) +
            '</span>' +
            (application.type === 'discount_code'
              ? '<button type="button" class="oddit-cart-drawer__discount-chip-remove" ' +
                  'data-cart-discount-remove="' + escapeHtml(application.title) + '" ' +
                  'aria-label="Remove discount ' + escapeHtml(application.title) + '">' +
                  '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
                    '<path d="M9 3 3 9M3 3l6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
                  '</svg>' +
                '</button>'
              : '') +
          '</li>'
        );
      })
      .join('');
    list.hidden = false;
  }

  function onDiscountSubmit(e) {
    var form = e.target.closest('[data-cart-discount-form]');
    if (!form) return;

    e.preventDefault();

    var input = form.querySelector('[data-cart-discount-input]');
    var button = form.querySelector('[data-cart-discount-apply]');
    var code = input ? input.value.trim() : '';

    if (!code) {
      setDiscountMessage('Enter a discount code.', true);
      if (input) input.focus();
      return;
    }

    if (button) {
      button.disabled = true;
      button.classList.add('is-loading');
    }
    setDiscountMessage('');

    applyDiscountCode(code)
      .then(function (cart) {
        renderCart(cart);

        if (findDiscountByCode(cart, code)) {
          setDiscountMessage('Discount applied.', false);
          if (input) input.value = '';
        } else {
          var messageEl = drawer.querySelector('[data-cart-discount-message]');
          setDiscountMessage(
            (messageEl && messageEl.getAttribute('data-invalid-message')) ||
              "That code doesn't apply to your cart. Using a gift card? You'll enter it at checkout.",
            true
          );
        }
      })
      .catch(function () {
        setDiscountMessage('We could not apply that code. Please try again.', true);
      })
      .then(function () {
        if (button) {
          button.disabled = false;
          button.classList.remove('is-loading');
        }
      });
  }

  // --------------------------------------------------------------------------
  // Render helpers
  // --------------------------------------------------------------------------
  function escapeHtml(str) {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getCartLevelAllocations(cart) {
    var allocations = {};
    var items = (cart.items || []).filter(function (item) {
      return !item.gift_card && Number(item.final_line_price) > 0;
    });

    (cart.cart_level_discount_applications || []).forEach(function (application) {
      var amount = Number(application.total_allocated_amount) || 0;
      if (amount <= 0 || !items.length) return;

      var base = items.reduce(function (sum, item) {
        return sum + Number(item.final_line_price);
      }, 0);
      var remaining = amount;

      items.forEach(function (item, index) {
        var share =
          index === items.length - 1
            ? remaining
            : Math.min(remaining, Math.round((amount * Number(item.final_line_price)) / base));
        allocations[item.key] = (allocations[item.key] || 0) + share;
        remaining -= share;
      });
    });

    return allocations;
  }

  function buildLineItemHTML(item, cartLevelDiscount) {
    var imageHTML = '';
    if (item.image || item.featured_image) {
      var imgSrc = item.image || (item.featured_image && item.featured_image.url) || '';
      var imgAlt = (item.featured_image && item.featured_image.alt) || item.product_title || '';
      if (imgSrc) {
        // Request a sensible size from Shopify CDN
        var sizedSrc = imgSrc.replace(/(\.(?:png|jpg|jpeg|webp|gif))/i, '_160x$1');
        imageHTML =
          '<img src="' +
          escapeHtml(sizedSrc) +
          '" alt="' +
          escapeHtml(imgAlt) +
          '" loading="lazy" width="80" height="80" class="oddit-cart-drawer__item-image">';
      }
    }

    var variantHTML = '';
    if (item.variant_title && item.variant_title.indexOf('Default') === -1) {
      variantHTML =
        '<p class="oddit-cart-drawer__item-variant">' +
        escapeHtml(item.variant_title) +
        '</p>';
    }

    var sellingPlanHTML = '';
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      sellingPlanHTML =
        '<p class="oddit-cart-drawer__item-subscription">' +
        escapeHtml(item.selling_plan_allocation.selling_plan.name) +
        '</p>';
    }

    var productTitle = item.product_title || (item.product && item.product.title) || '';
    var productUrl = item.url || '#';
    var key = item.key;

    var lineTotal =
      typeof item.final_line_price === 'number' ? item.final_line_price : item.line_price;

    var originalTotal =
      typeof item.original_line_price === 'number' ? item.original_line_price : lineTotal;

    lineTotal = Math.max(lineTotal - (cartLevelDiscount || 0), 0);

    var priceHTML;
    if (originalTotal > lineTotal && originalTotal > 0) {
      var discountPercent = Math.round(((originalTotal - lineTotal) / originalTotal) * 100);
      priceHTML =
        '<s class="oddit-cart-drawer__item-price-original">' +
          formatMoney(originalTotal) +
        '</s>' +
        '<span class="oddit-cart-drawer__item-price-final oddit-cart-drawer__item-price-final--discounted">' +
          formatMoney(lineTotal) +
        '</span>' +
        (discountPercent >= 1
          ? '<span class="oddit-cart-drawer__item-discount-badge">' + discountPercent + '% OFF</span>'
          : '');
    } else {
      priceHTML =
        '<span class="oddit-cart-drawer__item-price-final">' + formatMoney(lineTotal) + '</span>';
    }

    return (
      '<article class="oddit-cart-drawer__item" data-line-item data-line-item-key="' +
      escapeHtml(key) +
      '">' +
        '<div class="oddit-cart-drawer__item-image-wrap">' +
          imageHTML +
        '</div>' +
        '<div class="oddit-cart-drawer__item-details">' +
          '<div class="oddit-cart-drawer__item-info-row">' +
            '<div class="oddit-cart-drawer__item-info">' +
              '<h3 class="oddit-cart-drawer__item-title">' +
                '<a href="' + escapeHtml(productUrl) + '">' + escapeHtml(productTitle) + '</a>' +
              '</h3>' +
              variantHTML +
              sellingPlanHTML +
            '</div>' +
            '<button type="button" class="oddit-cart-drawer__item-remove" ' +
              'data-remove-item data-remove-line data-line-key="' + escapeHtml(key) + '" ' +
              'data-line-item-key="' + escapeHtml(key) + '" ' +
              'aria-label="Remove ' + escapeHtml(productTitle) + ' from cart">' +
              '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
                '<g opacity="0.5">' +
                  '<path d="M13.4961 4.5V13.5C13.4961 14.3284 12.8245 15 11.9961 15H5.99609C5.16767 15 4.49609 14.3284 4.49609 13.5V4.5M2.99609 4.5H14.9961M11.2461 4.5V3.75C11.2461 2.92157 10.5745 2.25 9.74609 2.25H8.24609C7.41767 2.25 6.74609 2.92157 6.74609 3.75V4.5" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>' +
                '</g>' +
              '</svg>' +
            '</button>' +
          '</div>' +
          '<div class="oddit-cart-drawer__item-actions">' +
            '<div class="oddit-cart-drawer__quantity">' +
              '<button type="button" class="oddit-cart-drawer__quantity-btn" ' +
                'data-quantity-decrease data-quantity-action="decrease" ' +
                'data-line-key="' + escapeHtml(key) + '" ' +
                'data-line-item-key="' + escapeHtml(key) + '" ' +
                'aria-label="Decrease quantity of ' + escapeHtml(productTitle) + '">' +
                '<svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
                  '<path d="M0.75 1H11.25" stroke="#272727" stroke-width="1.5" stroke-linecap="round"/>' +
                '</svg>' +
              '</button>' +
              '<span class="oddit-cart-drawer__quantity-value" data-quantity-value>' +
                Number(item.quantity) +
              '</span>' +
              '<button type="button" class="oddit-cart-drawer__quantity-btn" ' +
                'data-quantity-increase data-quantity-action="increase" ' +
                'data-line-key="' + escapeHtml(key) + '" ' +
                'data-line-item-key="' + escapeHtml(key) + '" ' +
                'aria-label="Increase quantity of ' + escapeHtml(productTitle) + '">' +
                '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
                  '<path d="M6 0.75V11.25" stroke="#272727" stroke-width="1.5" stroke-linecap="round"/>' +
                  '<path d="M0.75 6H11.25" stroke="#272727" stroke-width="1.5" stroke-linecap="round"/>' +
                '</svg>' +
              '</button>' +
            '</div>' +
            '<p class="oddit-cart-drawer__item-price" data-line-item-price>' +
              priceHTML +
            '</p>' +
          '</div>' +
        '</div>' +
      '</article>'
    );
  }

  // --------------------------------------------------------------------------
  // Progress bar
  // --------------------------------------------------------------------------
  function updateProgressBar(cart) {
    if (!freeShippingThreshold || freeShippingThreshold <= 0) return;

    var thresholdCents = freeShippingThreshold; // already in cents (data attr from Liquid)
    var totalCents = Number((cart && cart.total_price) || 0);
    var percentage = Math.min((totalCents / thresholdCents) * 100, 100);
    var remainingCents = Math.max(thresholdCents - totalCents, 0);

    var fillEls = drawer.querySelectorAll(
      '.oddit-cart-drawer__shipping-fill, [data-cart-progress-bar]'
    );
    fillEls.forEach(function (el) {
      el.style.width = percentage + '%';
    });

    var trackEls = drawer.querySelectorAll('.oddit-cart-drawer__shipping-track');
    trackEls.forEach(function (el) {
      el.setAttribute('aria-valuenow', String(Math.round(percentage)));
    });

    var labelEls = drawer.querySelectorAll(
      '.oddit-cart-drawer__shipping-text, [data-cart-progress-label]'
    );
    var successTemplate =
      drawer.getAttribute('data-free-shipping-success-message') ||
      '<p>You qualify for <strong>FREE</strong> shipping!</p>';
    var messageTemplate =
      drawer.getAttribute('data-free-shipping-message') ||
      '<p>You are <strong>[amount]</strong> away from FREE shipping!</p>';

    labelEls.forEach(function (el) {
      if (remainingCents <= 0) {
        // Keep existing markup if no template attribute, but try to detect success state
        if (drawer.hasAttribute('data-free-shipping-success-message')) {
          el.innerHTML = successTemplate;
        } else {
          el.innerHTML = '<p>You qualify for <strong>FREE</strong> shipping!</p>';
        }
      } else {
        var amountStr = formatMoney(remainingCents);
        if (drawer.hasAttribute('data-free-shipping-message')) {
          el.innerHTML = messageTemplate.replace(/\[amount\]/g, amountStr);
        } else {
          el.innerHTML =
            '<p>You are <strong>' +
            escapeHtml(amountStr) +
            '</strong> away from FREE shipping!</p>';
        }
      }
    });
  }

  // --------------------------------------------------------------------------
  // Upsell / cross-sell filtering
  // --------------------------------------------------------------------------
  function filterUpsells(cart) {
    var productIdsInCart = (cart.items || []).map(function (i) {
      return String(i.product_id);
    });
    var upsellCards = drawer.querySelectorAll('[data-product-id]');
    upsellCards.forEach(function (card) {
      var pid = String(card.getAttribute('data-product-id'));
      var inCart = productIdsInCart.indexOf(pid) !== -1;
      card.hidden = inCart;
      card.style.display = inCart ? 'none' : '';
    });
  }

  // --------------------------------------------------------------------------
  // The single renderer for all cart state -> DOM changes
  // --------------------------------------------------------------------------
  function renderCart(cart) {
    if (!cart) return;
    currentCart = cart;

    if (cart.currency) {
      activeCurrency = cart.currency;
    }

    // 1. Render line items into all [data-cart-items] containers
    var itemsContainers = drawer.querySelectorAll('[data-cart-items]');
    var hasItems = cart.item_count > 0;

    itemsContainers.forEach(function (container) {
      if (hasItems) {
        var html = '';
        var cartLevelAllocations = getCartLevelAllocations(cart);
        for (var i = 0; i < cart.items.length; i++) {
          html += buildLineItemHTML(cart.items[i], cartLevelAllocations[cart.items[i].key]);
        }
        container.innerHTML = html;
      } else {
        // The empty state lives in its own persistent [data-cart-empty]
        // sibling that setState() toggles — just clear the line items here.
        container.innerHTML = '';
      }
    });

    // 2. Item count badges
    var countEls = drawer.querySelectorAll(
      '.oddit-cart-drawer__count, .oddit-cart-drawer__subtotal-count, [data-cart-total-items], [data-cart-count]'
    );
    countEls.forEach(function (el) {
      el.textContent = String(cart.item_count);
    });

    // Also broadcast to any out-of-drawer count badges
    var globalCountEls = document.querySelectorAll('[data-global-cart-count]');
    globalCountEls.forEach(function (el) {
      el.textContent = String(cart.item_count);
    });

    // 3. Subtotal — NEVER touch [data-cart-additional-checkout] or its ancestors
    var subtotalEls = drawer.querySelectorAll('[data-cart-subtotal]');
    subtotalEls.forEach(function (el) {
      el.textContent = formatMoney(cart.total_price);
    });

    // 4. Progress bar
    updateProgressBar(cart);

    updateDiscountUI(cart);

    // 5. State containers (empty vs filled footer)
    setState(cart.item_count);

    // 6. Upsell filtering
    filterUpsells(cart);

    // 7. Custom event for header cart badge, etc.
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
  }

  // --------------------------------------------------------------------------
  // Event handlers — delegated where possible
  // --------------------------------------------------------------------------
  function onDrawerClick(e) {
    // Close button / overlay
    var closeTrigger = e.target.closest('[data-cart-drawer-close]');
    if (closeTrigger) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    // Remove an applied discount code
    var discountRemoveBtn = e.target.closest('[data-cart-discount-remove]');
    if (discountRemoveBtn) {
      e.preventDefault();
      if (discountRemoveBtn.disabled) return;
      var discountCode = discountRemoveBtn.getAttribute('data-cart-discount-remove');
      if (!discountCode) return;
      discountRemoveBtn.disabled = true;
      setDiscountMessage('');
      removeDiscountCode(discountCode)
        .then(function (cart) {
          renderCart(cart);
        })
        .catch(function () {
          setDiscountMessage('We could not remove that code. Please try again.', true);
          discountRemoveBtn.disabled = false;
        });
      return;
    }

    // Remove line item
    var removeBtn = e.target.closest('[data-remove-item], [data-remove-line]');
    if (removeBtn) {
      e.preventDefault();
      var rk = removeBtn.getAttribute('data-line-item-key') || removeBtn.getAttribute('data-line-key');
      if (!rk) return;
      removeBtn.disabled = true;
      removeFromCart(rk).catch(function () {
        removeBtn.disabled = false;
      });
      return;
    }

    // Quantity controls
    var qtyBtn = e.target.closest('[data-quantity-increase], [data-quantity-decrease], [data-quantity-action]');
    if (qtyBtn) {
      e.preventDefault();
      var qk = qtyBtn.getAttribute('data-line-item-key') || qtyBtn.getAttribute('data-line-key');
      if (!qk) return;
      var article = qtyBtn.closest('[data-line-item]');
      var qtyEl = article && article.querySelector('[data-quantity-value]');
      var current = qtyEl ? parseInt(qtyEl.textContent, 10) || 0 : 0;

      var action =
        qtyBtn.getAttribute('data-quantity-action') ||
        (qtyBtn.hasAttribute('data-quantity-increase') ? 'increase' : 'decrease');

      var nextQty = action === 'increase' ? current + 1 : Math.max(current - 1, 0);
      qtyBtn.disabled = true;
      updateLineItemQuantity(qk, nextQty)
        .catch(function () {
          /* swallow */
        })
        .then(function () {
          qtyBtn.disabled = false;
        });
      return;
    }

    // Cross-sell / upsell add buttons
    var addBtn = e.target.closest(
      '[data-add-to-cart][data-variant-id], .oddit-cart-drawer__rec-add[data-variant-id], .oddit-cart-drawer__addon-add[data-variant-id], .oddit-cart-drawer__upsell-cta[data-variant-id]'
    );
    if (addBtn) {
      e.preventDefault();
      var variantId = addBtn.getAttribute('data-variant-id');
      if (!variantId) return;
      addBtn.disabled = true;
      addToCart(Number(variantId))
        .catch(function () {
          /* swallow */
        })
        .then(function () {
          addBtn.disabled = false;
        });
      return;
    }

    // Subscription switch CTA
    var subBtn = e.target.closest('.oddit-cart-drawer__switch-cta[data-selling-plan-id]');
    if (subBtn) {
      e.preventDefault();
      var planId = subBtn.getAttribute('data-selling-plan-id');
      var targetLineKey = subBtn.getAttribute('data-line-item-key') || subBtn.getAttribute('data-line-key');
      if (!planId || !targetLineKey) return;
      subBtn.disabled = true;
      updateLineItemToSubscription(targetLineKey, Number(planId))
        .catch(function () {
          /* swallow */
        })
        .then(function () {
          subBtn.disabled = false;
        });
      return;
    }

    // Checkout button — go to /checkout. Show a loading state for the brief
    // interval before the browser navigates away so the click feels responsive.
    var checkoutBtn = e.target.closest('[data-cart-checkout], .oddit-cart-drawer__checkout-cta, .oddit-cart-drawer__checkout-btn');
    if (checkoutBtn) {
      e.preventDefault();
      if (checkoutBtn.classList.contains('is-loading')) return;
      checkoutBtn.classList.add('is-loading');
      checkoutBtn.setAttribute('aria-busy', 'true');
      window.location.href = '/checkout';
      return;
    }
  }

  function onKeyDown(e) {
    if (!drawer.classList.contains('is-open')) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      closeDrawer();
    } else if (e.key === 'Tab') {
      trapFocus(e);
    }
  }

  function resolveAddQuantity(button) {
    var scope = button.closest('[data-buy-buttons]');
    var input = scope ? scope.querySelector('[data-quantity-input]') : null;

    if (!input && button.closest('[data-sticky-cta]')) {
      input = document.querySelector('[data-buy-buttons] [data-quantity-input]');
    }

    if (!input) return 1;

    var parsed = parseInt(input.value, 10);
    return isNaN(parsed) || parsed < 1 ? 1 : parsed;
  }

  // External cart-trigger buttons (header icon, etc.)
  function onDocumentClick(e) {
    var trigger = e.target.closest('[data-cart-drawer-open], [data-cart-toggle], [data-cart-open]');
    if (trigger) {
      e.preventDefault();
      openDrawer();
      return;
    }

    // Add-to-cart buttons rendered outside the drawer (PDP buy buttons, sticky
    // CTA, essential add-ons). These are plain <button> elements, not /cart/add
    // form submits, so neither onFormSubmit nor the drawer-scoped onDrawerClick
    // catches them. Buttons inside the drawer are handled by onDrawerClick — skip
    // them here so a single click never adds the item twice.
    var addBtn = e.target.closest('[data-add-to-cart][data-variant-id]');
    if (addBtn && !addBtn.closest('[data-cart-drawer]')) {
      if (addBtn.closest('form[action*="/cart/add"]')) return;
      e.preventDefault();
      if (addBtn.disabled) return;
      var variantId = addBtn.getAttribute('data-variant-id');
      if (!variantId) return;
      addBtn.disabled = true;
      addToCart(Number(variantId), resolveAddQuantity(addBtn))
        .catch(function () {
          /* swallow — re-enable below so the shopper can retry */
        })
        .then(function () {
          addBtn.disabled = false;
        });
    }
  }

  // PDP / quick-add forms that POST to /cart/add — intercept and route through the drawer
  function onFormSubmit(e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.action || form.action.indexOf('/cart/add') === -1) return;
    if (form.hasAttribute('data-cart-bypass-drawer')) return;

    e.preventDefault();

    var formData = new FormData(form);
    var submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: formData
    })
      .then(handleResponse)
      .then(function () {
        return getCart();
      })
      .then(function (cart) {
        renderCart(cart);
        openDrawer();
      })
      .catch(function () {
        /* swallow */
      })
      .then(function () {
        if (submitBtn) submitBtn.disabled = false;
      });
  }

  // --------------------------------------------------------------------------
  // Init / teardown
  // --------------------------------------------------------------------------
  function init() {
    drawer.addEventListener('click', onDrawerClick, { signal: signal });
    drawer.addEventListener('submit', onDiscountSubmit, { signal: signal });
    document.addEventListener('keydown', onKeyDown, { signal: signal });
    document.addEventListener('click', onDocumentClick, { signal: signal });
    document.addEventListener('submit', onFormSubmit, { signal: signal });

    // Sync to current cart state on load (without opening the drawer)
    getCart()
      .then(function (cart) {
        renderCart(cart);
      })
      .catch(function () {
        /* no-op */
      });
  }

  function destroy() {
    controller.abort();
    document.body.style.overflow = '';
  }

  // --------------------------------------------------------------------------
  // Theme editor compatibility
  // --------------------------------------------------------------------------
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.contains(drawer)) {
      destroy();
      // Re-bootstrap by re-running the IIFE indirectly: reload the script tag
      var script = document.querySelector('script[src*="oddit-section-cart-drawer.js"]');
      if (script) {
        var clone = document.createElement('script');
        clone.src = script.src;
        clone.defer = true;
        script.parentNode.replaceChild(clone, script);
      }
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target && e.target.contains(drawer)) {
      destroy();
    }
  });

  // --------------------------------------------------------------------------
  // Public API
  // --------------------------------------------------------------------------
  window.OdditCartDrawer = {
    open: openDrawer,
    close: closeDrawer,
    toggle: toggleDrawer,
    addToCart: addToCart,
    addMultipleToCart: addMultipleToCart,
    removeFromCart: removeFromCart,
    updateLineItemQuantity: updateLineItemQuantity,
    updateLineItemToSubscription: updateLineItemToSubscription,
    getCart: getCart,
    refreshCart: refreshCart,
    renderCart: renderCart
  };

  // Backward-compat aliases
  window.odditCartDrawer = window.OdditCartDrawer;
  window.OdditCartDrawerPreview = window.OdditCartDrawer;

  init();
})();
